import { Dispatch, SetStateAction, useState } from 'react';
import { cn } from '@anytag/shared-utils';
import { useQueryHelper } from '@anytag/shared-hooks';
import { Button, BaseModal } from '@anytag/shared-atoms';
import { GeminiChat, namedOperations, useDeleteGeminiThreadMutation } from '@anytag/graphql';
import { ChatHistoryProps, EditingChatNameState } from './types';

interface ChatHistoryItemProps extends ChatHistoryProps {
  chat: GeminiChat;
  editingName: EditingChatNameState;
  isCurrent: boolean;
  setEditingName: Dispatch<SetStateAction<EditingChatNameState>>;
  onUpdateName: () => void;
}
export const ChatHistoryItem = ({
  chat,
  threadId,
  editingName,
  setEditingName,
  setChatHistoryThreadId,
  onUpdateName,
  isCurrent,
  setAiAssistantState
}: ChatHistoryItemProps) => {
  const { t, enqueueSnackbar } = useQueryHelper();
  const [deleteChatModal, setDeleteChatModal] = useState(false);
  const { callDeleteGeminiThread, loading } = useDeleteGeminiThreadMutation({
    refetchQueries: [namedOperations.Query.GeminiChats]
  });

  const handleDeleteChat = async () => {
    try {
      await callDeleteGeminiThread({ variables: { input: { id: chat.id } } });
      setDeleteChatModal(false);
    } catch (error) {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    }
  };

  return (
    <>
      <div
        className={cn(
          'group/item relative flex h-10 cursor-pointer items-center gap-x-1 rounded-sm px-2 py-1 text-base hover:bg-[#FAFAFA]',
          {
            'bg-[#F4F4F5] font-semibold': isCurrent,
            'border-base-border': editingName.isEditing
          }
        )}
        onClick={() => {
          if (!editingName.isEditing) {
            if (threadId !== chat.id) {
              // We need to save new threadId
              // Then close old chat thread and set new threadId on to url
              setChatHistoryThreadId(chat.id);
            } else {
              setAiAssistantState({ isChatHistoryOpen: false });
            }
          }
        }}
      >
        <span className="">-</span>
        {editingName.isEditing ? (
          <input
            autoFocus={editingName.isEditing}
            value={editingName.value || ''}
            disabled={editingName.isSubmitting}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                onUpdateName();
              }
            }}
            onChange={(e) => {
              setEditingName((prev) => ({
                ...prev,
                value: e.target.value,
                isEditing: true
              }));
            }}
            className="w-full"
          />
        ) : (
          <span className="w-full">{chat.name}</span>
        )}
        <div
          className={cn('absolute right-0 flex gap-2', 'invisible group-hover/item:visible')}
          onClick={(e) => e.stopPropagation()}
        >
          {editingName.isEditing ? (
            <Button
              variant="outline"
              size="sm"
              className={styles.hoverCardItem}
              loading={{
                showIcon: editingName.isSubmitting,
                status: editingName.isSubmitting
              }}
              onClick={onUpdateName}
              prefixIcon={{
                icon: 'refresh',
                size: '15px',
                className: cn('icon-color-gray')
              }}
            />
          ) : (
            <Button
              variant="outline"
              size="sm"
              className={styles.hoverCardItem}
              onClick={() => {
                setEditingName((prev) => ({
                  ...prev,
                  isEditing: true,
                  value: chat.name,
                  id: chat.id
                }));
              }}
              prefixIcon={{
                icon: 'pencil-underscore',
                size: '15px',
                className: cn('icon-color-gray')
              }}
            />
          )}
          <Button
            variant="outline"
            className={styles.hoverCardItem}
            onClick={() => setDeleteChatModal(true)}
            prefixIcon={{
              icon: 'trash',
              size: '15px',
              className: cn('icon-color-gray')
            }}
          />
        </div>
      </div>

      <BaseModal
        open={deleteChatModal}
        onClose={() => setDeleteChatModal(false)}
        title={t('Are you sure to delete this chat?')}
        description={
          <>
            <i className="text-base">{chat.name}</i>
            <p>{t('delete-ai-chat-description')}</p>
          </>
        }
        footer={{
          cancelButton: { onClick: () => setDeleteChatModal(false) },
          submitButton: {
            title: t('Button.Sure'),
            onClick: handleDeleteChat,
            loading: { showIcon: loading, status: loading }
          }
        }}
      />
    </>
  );
};

const styles = {
  hoverCardItem: cn('hover:!border-black-50 !size-6 !min-w-6 !bg-white')
};
